import { useEffect, useState } from "react";
import { useLocation } from "wouter";

import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, AlertTriangle, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/authContext";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/lib/i18n";
import { useShopOwnerFeatures } from "@/lib/featureFlags";

export default function VerificationSuccessPage() {
    const [, setLocation] = useLocation();
    const { user, refreshAuth } = useAuth();
    const { toast } = useToast();
    const { t } = useI18n();
    const { canSeeShopOwner } = useShopOwnerFeatures();
    const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
    const [errorMessage, setErrorMessage] = useState("");

    // Redirect if shop owner features are disabled
    if (!canSeeShopOwner) {
        setLocation("/");
        return null;
    }

    useEffect(() => {
        // Parse shopId from URL
        const searchParams = new URLSearchParams(window.location.search);
        const shopId = searchParams.get("shopId");
        const sessionId = searchParams.get("session_id"); // Stripe session ID often passed

        if (!shopId) {
            setStatus("error");
            setErrorMessage(t("verification.noShopId"));
            return;
        }

        const verifyPayment = async () => {
            try {
                console.log("Verifying payment status for shop:", shopId);

                // Call backend to force check/update status directly (bypassing slow webhook locally)
                const response = await fetch(`/api/shops/${shopId}/verify-payment-status`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sessionId })
                });

                // Even if manual verification fails (e.g. endpoint not yet ready), verify against user profile
                // but let's try to assume success if we are here from stripe
                if (response.ok) {
                    console.log("Payment verified via API");
                } else {
                    console.warn("Payment verification API check skipped/failed, relying on profile refresh");
                }

                // Refresh auth to get updated claimedShopIds
                await refreshAuth();

                // Give DB a moment to update if webhook is running parallel
                setTimeout(async () => {
                    await refreshAuth();
                    setStatus("success");
                    toast({
                        title: t("verification.successTitle"),
                        description: t("verification.successDesc"),
                    });
                }, 2000);

            } catch (error) {
                console.error("Verification error:", error);
                setStatus("error");
                setErrorMessage(t("verification.errorFetch"));
            }
        };

        if (user) {
            verifyPayment();
        } else {
            // Wait for auth to load? Or redirect?
            // Assuming auth context loads user eventually
        }
    }, [user, refreshAuth, toast, t]);

    if (status === "loading") {
        return (
            <div className="min-h-screen flex flex-col bg-background">
                
                <main className="flex-1 flex items-center justify-center">
                    <Card className="w-full max-w-md mx-4">
                        <CardContent className="py-12 flex flex-col items-center text-center">
                            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                            <h2 className="text-xl font-semibold mb-2">{t("verification.loadingTitle")}</h2>
                            <p className="text-muted-foreground">{t("verification.loadingDesc")}</p>
                        </CardContent>
                    </Card>
                </main>
                <Footer />
            </div>
        );
    }

    if (status === "error") {
        return (
            <div className="min-h-screen flex flex-col bg-background">
                
                <main className="flex-1 flex items-center justify-center">
                    <Card className="w-full max-w-md mx-4 border-destructive/20 bg-destructive/5">
                        <CardContent className="py-12 flex flex-col items-center text-center">
                            <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
                            <h2 className="text-xl font-semibold mb-2">{t("verification.errorTitle")}</h2>
                            <p className="text-muted-foreground mb-6">{errorMessage || t("verification.errorFallback")}</p>
                            <div className="flex gap-4">
                                <Button variant="outline" onClick={() => window.location.reload()}>{t("verification.retry")}</Button>
                                <Button onClick={() => setLocation("/account")}>{t("verification.goDashboard")}</Button>
                            </div>
                        </CardContent>
                    </Card>
                </main>
                <Footer />
            </div>
        );
    }

    // Get shop ID for the button link
    const shopId = new URLSearchParams(window.location.search).get("shopId");

    return (
        <div className="min-h-screen flex flex-col bg-background">
            
            <main className="flex-1 container mx-auto px-4 py-8">
                <div className="max-w-2xl mx-auto">
                    <Card className="border-green-200 bg-green-50/50">
                        <CardHeader className="text-center pb-2">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="h-8 w-8 text-green-600" />
                            </div>
                            <CardTitle className="text-2xl text-green-800">{t("verification.congrats")}</CardTitle>
                            <CardDescription className="text-green-700 text-lg">{t("verification.ownerNow")}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6 text-center">
                            <p className="text-muted-foreground">
                                {t("verification.paymentOk")}
                                {" "}
                                {t("verification.letterSent")}
                            </p>

                            <div className="bg-white p-6 rounded-lg border shadow-sm text-left space-y-4">
                                <h3 className="font-semibold text-lg">{t("verification.nextSteps")}</h3>
                                <ul className="space-y-3">
                                    <li className="flex gap-3">
                                        <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 font-bold text-xs">1</div>
                                        <div className="text-sm">
                                            <strong>{t("verification.step1")}:</strong> {t("verification.step1desc")}
                                        </div>
                                    </li>
                                    <li className="flex gap-3">
                                        <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 font-bold text-xs">2</div>
                                        <div className="text-sm">
                                            <strong>{t("verification.step2")}:</strong> {t("verification.step2desc")}
                                        </div>
                                    </li>
                                    <li className="flex gap-3">
                                        <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 font-bold text-xs">3</div>
                                        <div className="text-sm">
                                            <strong>{t("verification.step3")}!</strong> {t("verification.step3desc")}
                                        </div>
                                    </li>
                                </ul>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
                                <Button
                                    size="lg"
                                    className="w-full sm:w-auto"
                                    onClick={() => setLocation(shopId ? `/kringloopwinkels/shop/${shopId}` : "/account?tab=shop-admin")}
                                >
                                    {t("verification.goAdmin")}
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-full sm:w-auto"
                                    onClick={() => setLocation("/account")}
                                >
                                    {t("verification.myAccount")}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>
            <Footer />
        </div>
    );
}
